import type { LDFlagKeyMap } from "launchdarkly-react-client-sdk";
import { useFlags } from "launchdarkly-react-client-sdk";
import type { Event, ThemeTemplateOptions } from "src/types";
import { BlockVariant } from "src/types";

import useEvent from "./use-event";

export type ParsedDate = {
  value: Date | null;
  isValid: boolean;
  originalValue: string;
};

export type FactorDates = {
  creationDate?: string | null;
  deprecationDate?: string | null;
};

const parseDate = (str: string): ParsedDate => {
  try {
    const date = new Date(str);
    return {
      value: date,
      isValid: !isNaN(date.getTime()),
      originalValue: str,
    };
  } catch (e) {
    return { value: null, isValid: false, originalValue: str };
  }
};

const determineVisibility = (variant: BlockVariant, dates: FactorDates) => {
  const creationDate = parseDate(dates.creationDate);
  const deprecationDate = parseDate(dates.deprecationDate);

  const isManual = variant === BlockVariant.MANUAL;

  if (!deprecationDate.isValid || !creationDate.isValid) {
    return isManual;
  }
  if (isManual) {
    return creationDate.value < deprecationDate.value;
  }
  if (!isManual) {
    return creationDate.value > deprecationDate.value;
  }
};

const getFactorDates = ({
  event,
  flags,
}: {
  event: Event;
  flags: LDFlagKeyMap;
}): FactorDates => {
  return {
    creationDate: event.created_at,
    // TODO: remove this flag https://jira.ringcentral.com/browse/EVE-11873
    deprecationDate:
      flags.pageBuilderManualOrderWidgetsDeprecationDate ??
      "2024-07-01T00:00:00.0000",
  };
};

export const useWidgetDeprecation = () => {
  const { event } = useEvent();
  const flags = useFlags();
  const dates = getFactorDates({ event, flags });

  const isVisible = ({ variant }: { variant?: BlockVariant }) => {
    if (!variant) return true;

    // NOTE: we implement the flag evaluation here and not inside `determineVisibility`, because
    // `determineVisibility` is also called by `conditionalShow` and `conditionalShowList` which
    // are used on the `create-template.tsx` files which are not React components and therefore
    // they cannot check for flag values using the `useFlag` hook.
    // That means we cannot use widgets of the variant `SYNCED_EXT` on the default templates, but
    // that's ok since they're not as common.
    if (!flags.expandedSponsorTiers && variant === BlockVariant.SYNCED_EXT) {
      return false;
    }
    return determineVisibility(variant, dates);
  };

  return { isVisible };
};

export const conditionalShow = (
  variant: BlockVariant,
  options: ThemeTemplateOptions,
  element: JSX.Element
) => {
  const dates = getFactorDates(options);
  return determineVisibility(variant, dates) ? element : null;
};

export const conditionalShowList = (
  variant: BlockVariant,
  options: ThemeTemplateOptions,
  elements: JSX.Element[]
) => {
  const dates = getFactorDates(options);
  return determineVisibility(variant, dates) ? elements : [];
};
